import {
  Button,
  Flex,
  Modal,
  PasswordInput,
  Text,
  Textarea,
} from "@mantine/core";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { useIsOnline, usePostApi } from "src/hooks";
import { useSelector } from "src/redux/hooks";
import { SmallDivider } from "src/styled";

interface IWithdrawForm {
  reason: string;
  pass: string;
  pin: string;
}

interface Props {
  opened: boolean;
  close: () => void;
}

const WithdrawModal = ({ opened, close }: Props) => {
  const { pathname } = useLocation();
  const { idType, lbeidOrPartId, logbookId } = useParams();
  const { post, isLoading } = usePostApi();
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const { id, lbName } = useSelector(state => state.generatorSlice.latestEntry);
  const navigate = useNavigate();
  const isOnline = useIsOnline();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<IWithdrawForm>();

  useEffect(() => {
    if (opened) {
      reset();
    }
  }, [opened, reset]);

  const handleWithdraw = useCallback(
    async (data: IWithdrawForm) => {
      if (!data.pass && !data.pin) {
        setError("pass", { type: "required" });
        setError("pin", { type: "required" });
        return;
      }

      const params: any = {
        ...data,
      };

      if (idType && lbeidOrPartId) {
        params[String(idType)] = lbeidOrPartId;
      } else if (id) {
        params.lbeid = id;
      }

      await post({
        request: "withdraw-entry",
        body: {
          params,
          _lbname: lbName,
        },
        onSuccess: () => {
          close();
          if (pathname.includes("verify-entries")) {
            navigate("/summary");
          } else if (pathname.includes("logbook")) {
            if (lbeidOrPartId) {
              navigate(`/logbook/${logbookId}/${idType}`);
            } else {
              navigate(0);
            }
          } else {
            navigate("/");
          }
        },
      });
    },
    [
      close,
      id,
      idType,
      lbName,
      lbeidOrPartId,
      logbookId,
      navigate,
      pathname,
      post,
      setError,
    ],
  );

  return (
    <Modal
      size="50%"
      opened={opened}
      onClose={close}
      withCloseButton={false}
      zIndex={11}>
      <Text>Are you sure you want to withdraw this entry?</Text>
      <SmallDivider />
      <form>
        <Flex
          w="50%"
          mx="auto"
          p={10}
          pt={30}
          direction="column"
          align="center"
          gap={20}>
          <Flex direction="column" w="100%">
            <Text>Reason for the withdrawal</Text>
            <Textarea
              {...register("reason", { required: true })}
              styles={
                uppercase ? { input: { textTransform: "uppercase" } } : {}
              }
              error={!!errors.reason}
            />
          </Flex>
          <Flex w="100%" align="center" justify="space-between">
            <Text>Password</Text>
            <PasswordInput
              w="50%"
              {...register("pass")}
              error={!!errors.pass}
            />
          </Flex>
          {isOnline && (
            <Flex w="100%" align="center" justify="space-between">
              <Text>Pin</Text>
              <PasswordInput
                w="50%"
                {...register("pin")}
                error={!!errors.pin}
              />
            </Flex>
          )}
        </Flex>
        <Flex w="100%" align="center" justify="flex-end" gap={8} mt={20}>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <ConfirmationButton
            buttonProps={{
              variant: "filled",
              color: "red",
              loading: isLoading,
            }}
            modalText={`Do you wish to withdraw this entire entry?`}
            onConfirm={handleSubmit(handleWithdraw)}>
            Withdraw
          </ConfirmationButton>
        </Flex>
      </form>
    </Modal>
  );
};

export default WithdrawModal;
